import style from './index.module.css'

import isEqual from 'lodash/isEqual'
import React, { useState } from 'react'

import { hasFilterValue } from '@disco/utils/search-page'

import { FilterOperator, FilterState } from 'types/filter-page'

import { Box, Flex, Popover } from 'components/ds'

import { filterDataTypes } from '../config'
import { canCommitFilterValue } from '../utils'
import FilterBadgeTrigger from './filter-badge-trigger'
import FilterFooter from './filter-footer'

type Props = {
  value: FilterState
  onChange: (_value: FilterState) => void
  onDelete: (_value: FilterState) => void
}

const FilterBadge = ({ value, onChange, onDelete }: Props) => {
  const filterDefinition = filterDataTypes.find(
    filter => filter.id === value.definition.type,
  )
  const [tempValue, setTempValue] = useState(value.value)
  const [tempOperator, setTempOperator] = useState(value.operator)
  // Auto-open only for a freshly added, valueless filter.
  const [isOpen, setIsOpen] = useState(
    !value.definition.fixed && value.value === undefined,
  )
  const [isComponentValid, setIsComponentValid] = useState(true)

  const canCommit =
    canCommitFilterValue(value.definition.type, tempValue, tempOperator) &&
    isComponentValid

  const handleChange = (newValue: any, newOperator: FilterOperator) => {
    setTempValue(newValue)
    setTempOperator(newOperator)
  }

  const handleOpenChange = (isOpen: boolean) => {
    setIsOpen(isOpen)
    if (!isOpen) {
      if (!canCommit) {
        // Validation failed - don't commit the value
        return
      }

      // Validation passed - commit the value
      onChange({
        ...value,
        value: tempValue,
        operator: tempOperator,
      })
    } else {
      // Reset tempValue when reopening
      setTempValue(value.value)
      setTempOperator(value.operator)
      setIsComponentValid(true)
    }
  }

  const handleDelete = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
    onDelete(value)
  }

  const handleClearSelection = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
    if (value) {
      onChange({
        ...value,
        value: undefined,
        operator: undefined,
      })
    }
    setTempValue(undefined)
    setTempOperator(undefined)
  }

  const handleApplySelection = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
    if (!canCommit) {
      // Validation failed - don't commit the value
      return
    }
    if (value) {
      onChange({
        ...value,
        value: tempValue,
        operator: tempOperator,
      })
    }
    setIsOpen(false)
  }

  const showClearSelection =
    hasFilterValue(tempValue) || hasFilterValue(value.value)
  const isFilterUpdated = !isEqual(tempValue, value.value) && canCommit

  if (!filterDefinition) return null

  return (
    <Popover.Root open={isOpen} onOpenChange={handleOpenChange} modal={true}>
      <Popover.Trigger>
        <FilterBadgeTrigger
          filterState={value}
          filterDefinition={filterDefinition}
          onDelete={handleDelete}
        />
      </Popover.Trigger>
      <Popover.Content
        minWidth="240px"
        maxWidth="380px"
        maxHeight="400px"
        className={style.filterBadge_PopoverContent}
      >
        <Box className={style.filterBadge_ScrollArea}>
          <Flex width="100%" pb={showClearSelection ? '0' : '4'} asChild>
            <filterDefinition.component
              label={value.definition.label}
              field={value.definition.field}
              value={tempValue}
              operator={tempOperator}
              onChange={handleChange}
              onPress={() => handleOpenChange(false)}
              onValidityChange={setIsComponentValid}
            />
          </Flex>
        </Box>
        {showClearSelection && (
          <FilterFooter
            onClearSelection={handleClearSelection}
            onApplySelection={handleApplySelection}
            isFilterUpdated={isFilterUpdated}
          />
        )}
      </Popover.Content>
    </Popover.Root>
  )
}
export default FilterBadge
